/* eslint-disable flowtype/require-parameter-type */
// @flow

import * as React from 'react';
import styled from 'styled-components';
import ReactHTMLParser from 'react-html-parser';
import { Link } from '@latitude/link';
import {
  BUTTON_STYLE,
  BREAKPOINT,
  MARGIN,
  ALIGN,
  COLOR,
  TEXT_VARIANT,
  FONT_SIZE,
  DISPLAY,
  GUTTER,
  JUSTIFY_CONTENT,
  PADDING,
} from '@latitude/core/utils/constants';
import SvgInline from '@latitude/svg-inline';
import Image from '@latitude/image';
import { Box } from '@latitude/box';
import { Heading4, Heading5 } from '@latitude/heading';
import { Text } from '@latitude/text';

export type FeatureTileData = {
  icon: string,
  title: string,
  content: string,
  subcontent: string,
  ctaHref: string,
  ctaLabel: string,
  ctaTrackId?: string,
  ctaTrackEventData?: object,
  ctaTrackProductId?: array
};

export type FeatureTileProps = {
  /** tile icon name */
  icon: string,
  /** title */
  title: string,
  /** content */
  content: string,
  /** additional content area */
  subcontent?: string,
  /** CTA button label */
  buttonText: string,
  /** CTA button URL */
  buttonHref: string,
  /** CTA tracking id */
  buttonTrackId?: string,
  /** CTA tracking event data */
  buttonTrackEventData?: object,
  /** CTA tracking event array of product ID(s) */
  buttonTrackProductId?: array,
  /** Remove background styles */
  noBackgroundStyles?: boolean,
  /** Optional CSS class */
  className?: string,
  /** Optional background colour for feature tile item */
  tilesBgColour?: string
};

type Props = {
  /** Specify a unique id for the HTML element. */
  id?: string,
  /** The section heading. */
  heading?: string,
  /** The section subheading. */
  subheading?: string,
  /** Custom background colour */
  bgColor?: string,
  /** Number of tiles per row */
  tileColumns?: number,
  /** Tiles data */
  tiles: FeatureTileData[],
  /** Remove background styles */
  noBackgroundStyles?: boolean,
  /** Optional CSS class */
  className?: string,
  /** Optional background colour for feature tile item */
  tilesBgColour?: string
};

const parseContent = (content: FeatureTileProps) => {
  if (React.isValidElement(content)) {
    return content;
  }
  if (typeof content === 'string') {
    return ReactHTMLParser(content);
  }
  return '';
};

const FeatureTiles = ({
  id,
  heading,
  subheading,
  bgColor,
  tilesBgColour = COLOR.WHITE,
  tiles,
  tileColumns,
  noBackgroundStyles,
  className,
  useLocalAssets,
  isCompact,
}: Props) => {
  // console.log(isCompact);
  return (
    <Box.Section
      id={id}
      backgroundColor={bgColor || COLOR.GREY6}
      paddingTop={PADDING.P32}
      paddingBottom={PADDING.P32}
    >
      {heading && (
        <Heading subheading={!!subheading}>
          <span>{ReactHTMLParser(heading)}</span>
        </Heading>
      )}
      {subheading && (
        <Subheading variant={TEXT_VARIANT.DIV} fontSize={FONT_SIZE.LARGE}>
          {subheading}
        </Subheading>
      )}
      <FeatureTilesWrapper>
        {tiles.map((tile: FeatureTileData, index: number) => (
          <FeatureTile
            className={className}
            tilesBgColour={tilesBgColour}
            noBackgroundStyles={noBackgroundStyles}
            key={index}
            icon={tile.icon}
            title={tile.title}
            content={tile.content}
            subcontent={tile.subcontent}
            buttonHref={tile.ctaHref}
            buttonText={tile.ctaLabel}
            buttonTrackId={tile.ctaTrackId || ''}
            buttonTrackEventData={tile.ctaTrackEventData}
            buttonTrackProductId={tile.ctaTrackProductId}
            tileColumns={tileColumns}
            useLocalAssets={useLocalAssets}
            isCompact={isCompact}
          />
        ))}
      </FeatureTilesWrapper>
    </Box.Section>
  );
};

const FeatureTile = ({
  icon,
  title,
  content,
  tilesBgColour,
  buttonText,
  buttonHref,
  buttonTrackId,
  buttonTrackEventData,
  buttonTrackProductId,
  noBackgroundStyles,
  className,
  subcontent,
  useLocalAssets,
  isCompact,
}: FeatureTileProps) => {
  return (
    <StyledFeatureTile
      className={className}
      tilesBgColour={tilesBgColour}
      noBackgroundStyles={noBackgroundStyles}
      isCompact={isCompact}
    >
      <Box
        alignContent={ALIGN.CENTER}
        padding={PADDING.P24}
        backgroundColor={noBackgroundStyles ? COLOR.TRANSPARENT : COLOR.WHITE}
      >
        <Box
          margin={`${MARGIN.M24} 0 0`}
          display={DISPLAY.FLEX}
          justifyContent={JUSTIFY_CONTENT.CENTER}
        >
          {useLocalAssets ? (
            <Image src={icon} alt={title} />
          ) : (
            <StyledSvgInline
              name={icon}
              align={ALIGN.CENTER}
              isCompact={isCompact}
            />
          )}
        </Box>
        <StyledHeading5
          color={COLOR.BLACK}
          align={ALIGN.CENTER}
          marginTop={MARGIN.M16}
          marginBottom={MARGIN.M16}
          isCompact={isCompact}
        >
          {ReactHTMLParser(title)}
        </StyledHeading5>
        <Text align={ALIGN.CENTER}>{parseContent(content)}</Text>
      </Box>
      {buttonText && (
        <ButtonWrapper>
          <Link
            href={buttonHref}
            trackId={buttonTrackId || ''}
            trackEventData={buttonTrackEventData}
            trackProductId={buttonTrackProductId}
            button={BUTTON_STYLE.PRIMARY}
          >
            {buttonText}
          </Link>
        </ButtonWrapper>
      )}
      {subcontent && parseContent(subcontent)}
    </StyledFeatureTile>
  );
};

export default FeatureTiles;
export { FeatureTile };

const Heading = styled(Heading4)`
  text-align: ${ALIGN.CENTER};
  color: ${COLOR.BLACK};

  ${({ subheading }: { subheading: boolean }) =>
    subheading
      ? `margin-bottom: ${MARGIN.M8};`
      : `margin-bottom: ${MARGIN.M24};
         @media (min-width: ${BREAKPOINT.SM}) {
           margin-bottom: ${MARGIN.M40};
         }`}
`;

const Subheading = styled(Text)`
  text-align: ${ALIGN.CENTER};
  color: ${COLOR.GREY75};
  margin: 0 auto ${MARGIN.M24};
  max-width: 750px;
  @media (min-width: ${BREAKPOINT.SM}) {
    margin-bottom: ${MARGIN.M32};
  }
`;

export const FeatureTilesWrapper = styled(Box)`
  overflow: hidden;
  margin: -${GUTTER.G15} 0;

  /* change layout to grid from stacking list */
  @media (min-width: ${BREAKPOINT.MD}) {
    display: flex;
    flex-flow: row wrap;
    align-items: stretch;
    justify-content: center;
    margin: -${GUTTER.G15};
  }
`;

const featureTileBaseWidth = '350px';

const StyledFeatureTile = styled(Box)`
  /*
   * Remove all bg styling if prop is set to true
   */
  ${(props: Props) =>
    props.noBackgroundStyles
      ? `background-color: ${COLOR.TRANSPARENT};
     box-shadow: none;`
      : `background: ${props.tilesBgColour};
     box-shadow: 0 0 7px 0 rgba(0, 0, 0, 0.07);`}

  position: relative;
  width: 100%;
  max-width: ${featureTileBaseWidth};
  margin: ${GUTTER.G15} auto;

  /*
   * this is based on single line CTA height
   */

  padding-bottom: ${(props) => (props.isCompact ? '25px' : '80px')};

  ul {
    padding: 0;
  }

  li {
    display: flex;
    list-style: none;
    padding: 0;

    &:before {
      content: '•';
      color: ${COLOR.BLUE_LIGHT};
      font-size: 26px;
      margin-right: 8px;
    }
  }

  @media (min-width: ${BREAKPOINT.MD}) {
    flex: 1 1 auto;
    width: auto;
    margin: ${GUTTER.G15};
  }
`;

const StyledHeading5 = styled(Heading5)`
  font-size: ${(props) => (props.isCompact ? '20px' : '24px')};
  line-height: 32px;
`;

const ButtonWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  margin: auto;
  border-bottom: 32px solid transparent;

  /* override anchor styles */
  a.button {
    font-size: 16px;
    text-align: center;
    padding-left: 24px;
    padding-right: 24px;
    white-space: nowrap;
  }
`;

// @SP: Comment here...

const StyledSvgInline = styled(SvgInline)`
  width: ${(props) => (props.isCompact ? '90px' : 'auto')};
  height: auto;
`;
